import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 8px;
`;

const ContinueButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  margin-top: 0;
  margin-bottom: 24px;
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
  }
`;

// Bar Visualization Components (matching Optimal Pace Race style)
const BarContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.xl} auto 12px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  /* Allow scrolling when not interacting with slider */
  @media (max-width: ${theme.breakpoints.tablet}) {
    touch-action: pan-y;
  }
`;

const BarWrapper = styled.div`
  width: 80vw;
  max-width: 80vw;
  height: 40px;
  position: relative;
  background: #B8C5D1;
  border-radius: ${theme.borderRadius.lg};
  overflow: visible;
  box-shadow: ${theme.shadows.sm};
  touch-action: none;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw;
    max-width: 80vw;
    height: 50px; /* Slightly taller for easier touch interaction */
  }
`;

const ColorSegment = styled.div`
  position: absolute;
  top: 0;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  height: 100%;
  background: ${props => props.color};
  border-radius: ${props => {
    if (props.isFirst) return `${theme.borderRadius.lg} 0 0 ${theme.borderRadius.lg}`;
    if (props.isLast) return `0 ${theme.borderRadius.lg} ${theme.borderRadius.lg} 0`;
    return '0';
  }};
  opacity: 1;
  z-index: ${props => props.zIndex || 1};
`;

const SliderIndicator = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100px;
  height: 100px;
  background: white;
  border: 10px solid ${theme.colors.primary};
  border-radius: 50%;
  cursor: pointer;
  box-shadow: ${theme.shadows.md};
  z-index: 10;
  transition: all ${theme.animation.durations.fast};
  touch-action: none;
  user-select: none;
  
  /* Make it more obvious with a subtle inner highlight */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50px;
    height: 50px;
    background: ${theme.colors.primary};
    border-radius: 50%;
    opacity: 0.3;
  }
  
  /* Larger invisible touch target for mobile */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 150px;
    height: 150px;
    background: transparent;
    border-radius: 50%;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-50%) scale(1.1);
    box-shadow: ${theme.shadows.lg};
  }
  
  &:active {
    transform: translateY(-50%) scale(1.05);
  }
  
  /* Dragging state */
  ${props => props.isDragging && `
    transform: translateY(-50%) scale(1.15);
    box-shadow: ${theme.shadows.xl};
    border-color: ${theme.colors.accent};
  `}
  
  /* Smaller size for very narrow screens */
  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
    border-width: 8px;
    
    &::after {
      width: 40px;
      height: 40px;
    }
    
    &::before {
      width: 120px;
      height: 120px;
    }
  }
`;

const SliderLabel = styled.div`
  position: absolute;
  top: -46px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.primary};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  white-space: nowrap;
  z-index: 11;
  box-shadow: ${theme.shadows.sm};
  
  /* Arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.primary};
  }
`;

const BarLabels = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.doryDarkerBlue};
  font-weight: ${theme.typography.weights.medium};
  pointer-events: none;
  z-index: 10;
`;

const BarLabel = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  min-width: 20px;
`;

const LeftBarLabel = styled(BarLabel)`
  left: 5px;
  color: white !important;
  z-index: 25 !important;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 1) !important;
  background: rgba(0, 0, 0, 0.8) !important;
  padding: 4px 6px !important;
  border-radius: 4px !important;
  font-weight: bold !important;
  font-size: 14px !important;
  pointer-events: none !important;
`;

const RightBarLabel = styled(BarLabel)`
  right: 15px;
`;

const PerformanceText = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  padding: ${theme.spacing.md};
  margin-bottom: 10px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${props => props.borderColor || theme.colors.doryLighterBlue};
  max-width: 90%;
`;

const OptimalPaceLearningScreen = ({ data, onNext, userProgress = {} }) => {
  const [sliderValue, setSliderValue] = useState(7); // Start at position 7
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true); // Track if animation is running
  
  // Process merge fields and format text content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Get border color based on slider value
  const getBorderColor = (value) => {
    if (value >= 0 && value <= 3.3) return '#000000'; // Black
    if (value > 3.3 && value <= 5.5) return '#333333'; // Dark gray
    if (value > 5.5 && value <= 8.5) return '#666666'; // Medium gray
    if (value > 8.5 && value <= 11.5) return '#999999'; // Light gray
    if (value > 11.5 && value <= 15.5) return '#CCCCCC'; // Very light gray
    return '#B8C5D1'; // Default bar color for values above 15.5
  };

  // Auto-animation effect
  useEffect(() => {
    if (!isAnimating) return; // Stop if user has interacted
    
    let interval;
    
    // Wait 4 seconds before starting animation
    const startDelay = setTimeout(() => {
      interval = setInterval(() => {
        setSliderValue(prevValue => {
          if (prevValue >= 18) {
            setIsAnimating(false); // Stop animation when reaching 18
            return 18;
          }
          return prevValue + 1;
        });
      }, 2000); // Move every 2 seconds
    }, 4000); // Wait 4 seconds before starting

    return () => {
      clearTimeout(startDelay);
      if (interval) clearInterval(interval);
    };
  }, [isAnimating]);

  // Handle slider drag functionality
  const handleSliderDrag = (e) => {
    setIsAnimating(false); // Stop animation when user interacts
    const barWrapper = document.getElementById('optimal-pace-bar');
    if (!barWrapper) return;
    
    const rect = barWrapper.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clickX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newValue = Math.round(percentage * 18);
    
    setSliderValue(newValue);
  };

  // Handle mouse/touch events for the thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnimating(false); // Stop animation when user interacts
    setIsDragging(true);
    
    const handleMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSliderDrag(e);
    };
    const handleMouseUp = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleThumbTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnimating(false); // Stop animation when user interacts
    setIsDragging(true);
    
    const handleTouchMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSliderDrag(e);
    };
    const handleTouchEnd = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  // Handle clicking on the bar track
  const handleBarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnimating(false); // Stop animation when user interacts
    handleSliderDrag(e);
  };

  const handleBarTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnimating(false); // Stop animation when user interacts
    handleSliderDrag(e);
  };

  // Get performance description based on slider value
  const getPerformanceDescription = (value) => {
    if (value >= 0 && value <= 3) {
      return `<strong>Very Low Performance</strong><br/>Hard to keep up with normal demands. No capacity for unexpected situations.`;
    } else if (value >= 4 && value <= 5) {
      return `<strong>Low Performance</strong><br/>Normal work moves slowly and unexpected demands fall behind.`;
    } else if (value >= 6 && value <= 8) {
      return `<strong>Moderate Performance</strong><br/>Normal work is manageable as long as no unexpected situations come up.`;
    } else if (value >= 9 && value <= 11) {
      return `<strong>Solid Performance</strong><br/>Normal work starts to feel easy, space for creativity opens up, and one or two unexpected situations can be managed well.`;
    } else if (value >= 12 && value <= 15) {
      return `<strong>Strong Performance</strong><br/>Normal work is easy, large creative bursts are possible, and many unexpected situations can be managed well.`;
    } else if (value >= 16 && value <= 18) {
      return `<strong>Superhuman Performance</strong><br/>Normal work becomes effortless, creative bursts become easy, and no unexpected situation is large enough to cause problems.`;
    }
    return `<strong>Performance level.</strong>`;
  };

  // Calculate position of slider indicator (0-18 scale) - moved 20px left
  const indicatorLeft = `${2 + (sliderValue / 18) * 92 - 2.5}%`;

  // Render color segments for different performance levels
  const renderColorSegments = () => {
    // Define continuous segments that cover the entire 0-18 range
    const segments = [
      { range: [0, 3], color: theme.colors.error, label: 'Very Low' },
      { range: [3, 5], color: theme.colors.warning, label: 'Low' },
      { range: [5, 8], color: theme.colors.info, label: 'Moderate' },
      { range: [8, 11], color: theme.colors.warning, label: 'Solid' },
      { range: [11, 15], color: theme.colors.accent, label: 'Strong' },
      { range: [15, 18], color: theme.colors.warning, label: 'Superhuman' }
    ];

    return segments.map((segment, index) => {
      // Calculate the total bar coverage area - same as slider calculation
      const totalBarWidth = 92; // Total usable width of the bar
      const barStartOffset = 2 - 2.5; // Adjusted for the 20px left shift
      const rightShift = 3; // 30px right shift for internal segments
      
      // Calculate the start and end positions for this segment
      let segmentStart, segmentEnd;
      
      if (index === 0) {
        // First segment: start at the far left of the bar
        segmentStart = (segment.range[0] / 18) * totalBarWidth + barStartOffset;
        segmentEnd = (segment.range[1] / 18) * totalBarWidth + barStartOffset;
      } else {
        // Other segments: apply the right shift
        segmentStart = (segment.range[0] / 18) * totalBarWidth + barStartOffset + rightShift;
        segmentEnd = (segment.range[1] / 18) * totalBarWidth + barStartOffset + rightShift;
      }
      
      return (
        <ColorSegment
          key={index}
          left={segmentStart}
          width={segmentEnd - segmentStart}
          color={segment.color}
          isFirst={index === 0}
          isLast={index === segments.length - 1}
        />
      );
    });
  };

  return (
    <Container>
      <Content>
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* Interactive Optimal Pace Bar */}
        <BarContainer>
          <BarWrapper 
            id="optimal-pace-bar"
            onClick={handleBarClick}
            onTouchStart={handleBarTouchStart}
          >
            {/* Dark brand color segment from 0 to 3 */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '0%',
                width: `${(3.3 / 18) * 92}%`,
                height: '100%',
                backgroundColor: '#000000',
                borderRadius: '8px 0 0 8px',
                zIndex: 10
              }}
            />
            
            {/* Second color segment from 3.5 to 5 */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `${(3.3 / 18) * 92}%`,
                width: `${(5.5 - 3.5) / 18 * 92}%`,
                height: '100%',
                backgroundColor: '#333333',
                borderRadius: '0',
                zIndex: 10
              }}
            />
            
            {/* Third color segment from 5.5 to 8 */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `${(5.30 / 18) * 92}%`,
                width: `${(8.5 - 5.5) / 18 * 92}%`,
                height: '100%',
                backgroundColor: '#666666',
                borderRadius: '0',
                zIndex: 10
              }}
            />
            
            {/* Fourth color segment from 8.5 to 11 */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `${(8.30 / 18) * 92}%`,
                width: `${(11.5  - 8.5) / 18 * 92}%`,
                height: '100%',
                backgroundColor: '#999999',
                borderRadius: '0',
                zIndex: 10
              }}
            />
            
            {/* Fifth color segment from 11.5 to 16 */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `${(11.3 / 18) * 92}%`,
                width: `${(15.5 - 11.5) / 18 * 92}%`,
                height: '100%',
                backgroundColor: '#CCCCCC',
                borderRadius: '0',
                zIndex: 10
              }}
            />
            
            <BarLabels>
              <LeftBarLabel>0</LeftBarLabel>
              <RightBarLabel>18</RightBarLabel>
            </BarLabels>
            
            <SliderIndicator 
              style={{ left: indicatorLeft }}
              isDragging={isDragging}
              onMouseDown={handleThumbMouseDown}
              onTouchStart={handleThumbTouchStart}
            >
              <SliderLabel>{sliderValue}</SliderLabel>
            </SliderIndicator>
          </BarWrapper>
        </BarContainer>
        
        {/* Performance Description */}
        <PerformanceText 
          dangerouslySetInnerHTML={{ __html: getPerformanceDescription(sliderValue) }}
          borderColor={getBorderColor(sliderValue)}
        />
        
        {/* CTA button appears right after content */}
        <ContinueButton onClick={() => onNext?.(data.key)}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default OptimalPaceLearningScreen;
